
import React from 'react';
import { useLanguage } from '@/contexts/LanguageContext';
import { Button } from '@/components/ui/button';
import { Link } from 'react-router-dom';

const Hero = () => {
  const { t, language } = useLanguage();

  return (
    <div className="relative min-h-screen flex items-center overflow-hidden pt-20">
      {/* Background Pattern */}
      <div className="absolute inset-0 bg-white">
        <div className="absolute inset-0 opacity-5" style={{ 
          backgroundImage: 'url("data:image/svg+xml,%3Csvg width=\'60\' height=\'60\' viewBox=\'0 0 60 60\' xmlns=\'http://www.w3.org/2000/svg\'%3E%3Cg fill=\'%23EF3E2C\' fill-opacity=\'0.4\' fill-rule=\'evenodd\'%3E%3Cpath d=\'M36 34v-4h-2v4h-4v2h4v4h2v-4h4v-2h-4zm0-30V0h-2v4h-4v2h4v4h2V6h4V4h-4zM6 34v-4H4v4H0v2h4v4h2v-4h4v-2H6zM6 4V0H4v4H0v2h4v4h2V6h4V4H6z\'/%3E%3C/g%3E%3C/svg%3E")',
          backgroundSize: '60px 60px'
        }}></div>
      </div>

      {/* Enhanced Background Elements */}
      <div className="absolute -right-10 -top-10 lg:right-0 lg:top-0 w-96 h-96 bg-attaboy/10 rounded-full filter blur-3xl"></div>
      <div className="absolute -left-10 bottom-10 w-72 h-72 bg-attaboy-light/10 rounded-full filter blur-3xl"></div>
      <div className="absolute top-40 left-1/4 w-64 h-64 bg-attaboy/5 rounded-full filter blur-2xl"></div>

      <div className="container mx-auto px-4 z-10 pt-10 pb-24 md:pb-32">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
          {/* Text Content */}
          <div className="animate-fade-in-left">
            <h1 className="text-4xl md:text-5xl lg:text-6xl font-bold mb-6 font-poppins text-attaboy-black leading-tight">
              {t('hero.title')}
            </h1>
            <p className="text-xl text-gray-600 mb-4 max-w-lg">
              {t('hero.subtitle')}
            </p>
            <p className="text-xl text-gray-600 mb-8 max-w-lg">
              {t('hero.description')}
            </p>
            <div className="flex flex-col sm:flex-row space-y-4 sm:space-y-0 sm:space-x-4">
              <Link to="/products">
                <Button className="cta-button text-base sm:text-lg px-6 py-4 w-full sm:w-auto">
                  {t('hero.cta.explore')}
                </Button>
              </Link>
              <Link to="/contact">
                <Button variant="outline" className="text-base sm:text-lg px-6 py-4 border-2 border-attaboy text-attaboy hover:bg-attaboy hover:text-white transition-all duration-300 w-full sm:w-auto">
                  {t('hero.cta.demo')}
                </Button>
              </Link>
            </div>
            
            {/* Trust Indicators */}
            <div className="grid grid-cols-3 gap-4 mt-16">
              <div className="flex flex-col items-center">
                <div className="bg-attaboy-faded p-3 rounded-full mb-3">
                  <svg className="h-6 w-6 text-attaboy" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor">
                    <path fillRule="evenodd" d="M11.3 1.046A1 1 0 0112 2v5h4a1 1 0 01.82 1.573l-7 10A1 1 0 018 18v-5H4a1 1 0 01-.82-1.573l7-10a1 1 0 011.12-.38z" clipRule="evenodd" />
                  </svg>
                </div>
                <h3 className="font-medium text-attaboy-black">{t('trust.innovation')}</h3>
              </div>
              <div className="flex flex-col items-center">
                <div className="bg-attaboy-faded p-3 rounded-full mb-3">
                  <svg className="h-6 w-6 text-attaboy" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor">
                    <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM7 9H5v2h2V9zm8 0h-2v2h2V9zM9 9h2v2H9V9z" clipRule="evenodd" />
                  </svg>
                </div>
                <h3 className="font-medium text-attaboy-black">{t('trust.simplicity')}</h3>
              </div>
              <div className="flex flex-col items-center">
                <div className="bg-attaboy-faded p-3 rounded-full mb-3">
                  <svg className="h-6 w-6 text-attaboy" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor">
                    <path fillRule="evenodd" d="M2.166 4.999A11.954 11.954 0 0010 1.944 11.954 11.954 0 0017.834 5c.11.65.166 1.32.166 2.001 0 5.225-3.34 9.67-8 11.317C5.34 16.67 2 12.225 2 7c0-.682.057-1.35.166-2.001zm11.541 3.708a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                  </svg>
                </div>
                <h3 className="font-medium text-attaboy-black">{t('trust.trust')}</h3>
              </div>
            </div>
          </div>
          
          {/* Logo Animation */}
          <div className="flex justify-center lg:justify-end animate-fade-in-right">
            <div className="relative h-64 w-64 md:h-80 md:w-80 animate-float">
              <div className="absolute inset-0">
                <svg
                  className="h-full w-full text-attaboy"
                  viewBox="0 0 200 200"
                  fill="currentColor"
                  xmlns="http://www.w3.org/2000/svg"
                >
                  {/* Updated SVG path to match the correct logo */}
                  <path d="M160 40 A40 40 0 1 1 160 120 A40 40 0 1 1 160 40 Z" />
                  <path d="M100 50 L40 170 L160 170 Z" />
                </svg>
              </div>
              <div className="absolute inset-0 opacity-30 blur-xl">
                <svg
                  className="h-full w-full text-attaboy-light"
                  viewBox="0 0 200 200"
                  fill="currentColor"
                  xmlns="http://www.w3.org/2000/svg"
                >
                  {/* Updated SVG path to match the correct logo */}
                  <path d="M160 40 A40 40 0 1 1 160 120 A40 40 0 1 1 160 40 Z" />
                  <path d="M100 50 L40 170 L160 170 Z" />
                </svg>
              </div>
            </div>
          </div>
        </div>
      </div>
      
      {/* Wave Separator */}
      <div className="absolute bottom-0 left-0 w-full overflow-hidden leading-none">
        <svg
          className="relative block w-full h-12 md:h-16"
          data-name="Layer 1"
          xmlns="http://www.w3.org/2000/svg"
          viewBox="0 0 1200 120"
          preserveAspectRatio="none"
        >
          <path
            d="M321.39,56.44c58-10.79,114.16-30.13,172-41.86,82.39-16.72,168.19-17.73,250.45-.39C823.78,31,906.67,72,985.66,92.83c70.05,18.48,146.53,26.09,214.34,3V0H0V27.35A600.21,600.21,0,0,0,321.39,56.44Z"
            className="fill-white"
          ></path>
        </svg>
      </div>
    </div>
  );
};

export default Hero;
